import React, { useEffect, useState } from "react";
import '../styles/ProofTemplates.css';
import { Spinner, Alert } from "react-bootstrap";
import {
  constructAndReturnValidationErrorMessage,
  renderPdf,
  formatPhoneNumber
} from "../utils/PreviewUtils";
import { Typography } from "@mui/material";
import {
  saveCustomizationToDB,
  generatePreviewPdf,
  fetchTemplatesConfig,
  fetchTemplateConfigDetails,
  getCustomizedTemplatesCreatedByUser,
  viewPreviewPdf
} from "../utils/api";
import TemplateSelector from "../components/TemplateSelector";
import PdfPreviewModal from "../components/PdfPreviewModal";
import ReusableForm from "../components/ReusableForm";
import PageHelmet from "../components/PageHelmet";
import { savedTemplatesRowMapping, savedTemplatesGridColumns, generateGridColumns, generateGridRows } from '../utils/PageUtils'
import CustomDataGrid from '../components/CustomDataGrid'
import ViewPdfModal from '../components/ViewPdfModal'


const ProofTemplates = ({ userRole, bearerToken }) => {
  const [state, setState] = useState({
    selectedTemplate: "",
    templateConfig: null,
    pdfBinaryData: {},
    isModalOpen: false,
    templateName: "",
    isLoading: false,
    pdfImageSrc: null,
    error: "",
    successMessage: "",
    pdfError: null,
    initialMessage: "Type of Template determines the content & font.",
  });
  const [lastGeneratedParams, setLastGeneratedParams] = useState({});
  const [selectedTemplateConfig, setSelectedTemplateConfig] = useState({});
  const [formFields, setFormFields] = useState({ contact: {}, customer: {} });
  const [formData, setFormData] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateConfigCache, setTemplateConfigCache] = useState({});

  const [savedTemplatesError, setSavedTemplatesError] = useState(null)
  const [savedTemplates, setSavedTemplates] = useState([])
  const [refreshRequests, setRefreshRequests] = useState(false)
  const [activeTab, setActiveTab] = useState('create'); // 'view' or 'create'
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPDF, setShowPDF] = useState(false);
  const [savedPdfImage, setSavedPdfImage] = useState(null)
  const [savedPDF, setSavedPDF] = useState(null)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchTemplatesForm = async () => {
      if (!bearerToken) {
        return;
      }
      setTemplatesLoading(true);
      try {
        const response = await fetchTemplatesConfig(bearerToken);
        if (response && response.status === 200) {
          const data = response.data;
          setFormData(data?.records);
        }
        else if (response.status == 404) {
          setTemplateError(response.data?.message);
        }
        else if (response.status == 500) {
          setTemplateError("The operation you attempted has failed. Please contact support.");
        }
        else {
          setTemplateError(response?.message || "Failed to fetch templates configuration");
        }
      } catch (error) {
        setTemplateError("The operation you attempted has failed. Please contact support.");
      } finally {
        setTemplatesLoading(false);
      }
    }

    fetchTemplatesForm();
  }, []);

  const handleInputChange = (e, section, key, type) => {
    const value = e.target.value;
    const valToSet = type === "phone" ? formatPhoneNumber(value) : value;

    setFormFields((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [key]: valToSet,
      },
    }));
  };

  const handleSelect = async (e) => {
    const fullValue = e.target.value;
    if (!fullValue) return;

    const [owner, templateName] = fullValue.split('|');
    const templateMeta = formData.find(t => t.name === templateName && t.owner === owner);

    if (!templateMeta) return;

    setState(prev => ({
      ...prev,
      selectedTemplate: fullValue,
      initialMessage: "",
      pdfError: null,
      isModalOpen: false,
      pdfImageSrc: null,
      pdfBinaryData: {},
    }));

    const cacheKey = fullValue;

    if (templateConfigCache[cacheKey]) {
      const { template, values } = templateConfigCache[cacheKey];
      setFormFields(values);
      setSelectedTemplateConfig(template);
      return;
    }

    try {
      setIsConfigLoading(true);

      const response = await fetchTemplateConfigDetails(
        owner,
        templateName,
        bearerToken
      );

      if (response?.status === 200) {
        const template = response.data;
        const values = {};
        const sections = {
          contact: template.contact || [],
          customer: template.customer || [],
        };

        Object.entries(sections).forEach(([key, value]) => {
          values[key] = value.reduce((acc, field) => {
            acc[field.name] = field.defaultValue ?? "";
            return acc;
          }, {});
        });

        setFormFields(values);
        setSelectedTemplateConfig(template);
        setTemplateConfigCache((prev) => ({
          ...prev,
          [cacheKey]: { template, values },
        }));
      }
      else if (response.status == 400) {
        setTemplateError(response.data?.message);
      }
      else if (response.status == 403) {
        setTemplateError(response.data?.message);
      }
      else if (response.status == 404) {
        setTemplateError(response.data?.message);
      }
      else if (response.status == 500) {
        setTemplateError("The operation you attempted has failed. Please contact support.");
      }
      else {
        setTemplateError("The operation you attempted has failed. Please contact support.");
      }
    } catch (err) {
      setTemplateError("The operation you attempted has failed. Please contact support.");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const trimTemplateData = (templateData) => {
    const trimmed = {};
    for (const sectionKey in templateData) {
      trimmed[sectionKey] = {};
      for (const fieldKey in templateData[sectionKey]) {
        const value = templateData[sectionKey][fieldKey];
        trimmed[sectionKey][fieldKey] =
          typeof value === "string" ? value.trim() : value;
      }
    }
    return trimmed;
  };

  const handleGeneratePreview = async () => {
    const trimmedValues = trimTemplateData(formFields);
    setFormFields(trimmedValues);

    const queryParams = {
      name: selectedTemplateConfig.name,
      owner: selectedTemplateConfig.owner,
      contact: trimmedValues.contact,
      customer: trimmedValues.customer,
    };

    const currentParamsString = JSON.stringify(queryParams);
    const lastParamsString = JSON.stringify(
      lastGeneratedParams[state.selectedTemplate] || {}
    );

    const existingPdf = state.pdfBinaryData[state.selectedTemplate];

    // Check if we can serve from cache
    if (currentParamsString === lastParamsString && existingPdf) {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      // Small delay to show the "Generating" spinner so the user knows it's working
      setTimeout(() => {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isModalOpen: true,
        }));
      }, 500);
      return;
    }

    // Otherwise, fetch new preview
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
      pdfError: null,
      successMessage: null,
    }));

    try {
      const newPdfContent = await generatePreviewPdf(queryParams, bearerToken);
      if (!newPdfContent.success) {
        setState((prevState) => ({
          ...prevState,
          pdfError: newPdfContent.message,
          isLoading: false,
        }));
        return;
      }
      setState((prevState) => ({
        ...prevState,
        pdfBinaryData: {
          ...prevState.pdfBinaryData,
          [state.selectedTemplate]: newPdfContent.data,
        },
      }));
      setLastGeneratedParams((prev) => ({
        ...prev,
        [state.selectedTemplate]: queryParams,
      }));
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        pdfError: error.message,
        isLoading: false,
      }));
    }
  };

  useEffect(() => {
    const renderPdfPreview = async () => {
      if (!state.pdfBinaryData[state.selectedTemplate]) return;

      try {
        const pdfImages = await renderPdf(
          state.pdfBinaryData[state.selectedTemplate]
        );
        setState((prevState) => ({
          ...prevState,
          pdfImageSrc: pdfImages,
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          pdfError: "Error rendering PDF preview"
        }));
      }
    };
    renderPdfPreview();
  }, [state.pdfBinaryData]);

  useEffect(() => {
    if (state.pdfBinaryData && Object.keys(state.pdfBinaryData).length > 0) {
      setState((prevState) => ({
        ...prevState,
        isModalOpen: true,
        isLoading: false,
      }));
    }
  }, [state.pdfBinaryData]);

  const handleClose = () =>
    setState((prevState) => ({ ...prevState, isModalOpen: false }));

  const handleTemplateSave = async () => {
    setState((prevState) => ({
      ...prevState,
      error: null,
      successMessage: null,
      templateName: "",
    }));

    const trimmedTemplateName = state.templateName?.trim();

    if (!trimmedTemplateName) {
      setState((prevState) => ({
        ...prevState,
        error: "Need a unique name to save Template",
      }));
      return;
    }
    if (trimmedTemplateName === "") {
      setState((prevState) => ({
        ...prevState,
        error: "Template name cannot be an empty",
      }));
      return;
    }

    try {
      const data = {
        contact: formFields.contact,
        customer: formFields.customer,
        template: selectedTemplateConfig.name,
        name: trimmedTemplateName,
        owner: selectedTemplateConfig.owner,
      }
      setState((prevState) => ({
        ...prevState,
        successMessage: null,
        error: null,
      }));
      const response = await saveCustomizationToDB(data, bearerToken);
      switch (response.status) {
        case 201:
          setState((prevState) => ({
            ...prevState,
            successMessage:
              "Template saved successfully",
            templateName: "",
          }));
          break;
        case 400:
          setState((prevState) => ({
            ...prevState,
            error:
              "The operation you attempted has failed. Please contact support.",
          }));
          break;
        case 403:
          setState((prevState) => ({
            ...prevState,
            error: `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}.\nContact Support to increase your Quota.`
          }));
          break;
        case 409:
          const message = `A template already exists for the given name ${state.templateName}. Please choose another name for this template.`;
          setState((prevState) => ({
            ...prevState,
            error: message,
          }));
          break;
        case 422:
          const constructErrorMesage = constructAndReturnValidationErrorMessage(
            response.data,
            "Your attempt to create a template failed due to invalid inputs"
          );
          setState((prevState) => ({
            ...prevState,
            error: constructErrorMesage,
          }));
          break;

        default:
          setState((prevState) => ({
            ...prevState,
            error:
              "The operation you attempted has failed. Please contact support.",
          }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        error: error.message,
      }));
    }
  };

  const handleDownloadPdf = () => {
    const arrayBuffer = state.pdfBinaryData[state.selectedTemplate];
    if (arrayBuffer) {
      try {
        const blob = new Blob([arrayBuffer], { type: "application/pdf" });
        const link = document.createElement("a");

        link.href = URL.createObjectURL(blob);
        link.download = `${selectedTemplateConfig.owner}_${selectedTemplateConfig.name}_preview`;
        link.click();
      } catch (error) {
        throw new Error("Error downloading PDF ");
      }
    }
  };

  useEffect(() => {
    const fetchSavedTemplates = async () => {
      setSavedTemplatesError(null)
      try {
        const response = await getCustomizedTemplatesCreatedByUser(bearerToken);
        if (!Array.isArray(response)) {
          setSavedTemplatesError(
            "Please create a customized template first and then come back here"
          );
          setSavedTemplates([]);
          return;
        }
        else {
          setSavedTemplates(response)
        }
      } catch {
        setSavedTemplatesError("The operation you attempted has failed. Please contact support.")
      }
    }
    if (refreshRequests) {
      fetchSavedTemplates()
      setRefreshRequests(false)
    }
  }, [refreshRequests]);

  useEffect(() => {
    const showSavedPDF = async () => {
      setSavedTemplatesError(null)
      if (!savedPDF) {
        return;
      }
      try {
        const pdfIamges = await renderPdf(savedPDF)
        setShowPDF(true);
        setSavedPdfImage(pdfIamges)
        setTemplateLoading(false)
      } catch {
        setSavedTemplatesError("The operation you attempted has failed. Please contact support.")
        setTemplateLoading(false)
      }

    }
    showSavedPDF();
  }, [savedPDF])

  const handleViewTemplatesClick = () => {
    setActiveTab('view');
    setRefreshRequests(true)
  }

  const handleCreateTemplatesClick = () => {
    setActiveTab('create');
  }

  const handleRowClick = (e) => {
    setSavedTemplatesError(null)
    if (selectedRowId === e.id) {
      setSelectedRowId(null)
      setSelectedTemplate(null)
    } else {
      setSelectedRowId(e.id)
      setSelectedTemplate(e.row)
    }
  }

  const handleViewPDF = async (selectedTemplate) => {
    setSavedTemplatesError(null)
    setTemplateLoading(true)
    if (!selectedTemplate) {
      setSavedTemplatesError("Please select a template to view the PDF.")
      setTemplateLoading(false)
      return;
    }
    const queryParams = {
      template_name: selectedTemplate.template_name,
      name: selectedTemplate.choice,
      owner: selectedTemplate.owner,
    }

    const pdfResponse = await viewPreviewPdf(queryParams, bearerToken)
    if (!pdfResponse.success) {
      setSavedTemplatesError(pdfResponse.message)
      setTemplateLoading(false)
      return
    }
    setSavedPDF(pdfResponse.data)
  }

  const closeShowPDF = () => {
    setShowPDF(false)
  }

  const savedTemplatesRows = generateGridRows(savedTemplates, savedTemplatesRowMapping)
  const savedTemplatesColumns = generateGridColumns(savedTemplatesGridColumns, 'template_name')


  return userRole === "customer" ? (
    <div className="dashboard-container">
      <PageHelmet
        title="Proof Template"
        description="Choose and Save templates with TraQR."
        url={`${process.env.REACT_APP_REDIRECT_URI}/proof_templates`}
      />
      <div className="proof-title-section">
        <h1 className="main-heading">Create & Proof Your Template</h1>
      </div>

      <div className={`templates-tab ${activeTab === "view" ? "view-active" : ""}`}>
        <button
          onClick={handleCreateTemplatesClick}
          className={`tab-button ${activeTab === "create" ? "active" : ""}`}
        >
          Create Template
        </button>
        <button
          onClick={handleViewTemplatesClick}
          className={`tab-button ${activeTab === "view" ? "active" : ""}`}
        >
          Saved Templates
        </button>
      </div>

      {
        activeTab === 'view' && (
          <>
            {
              templateLoading &&
              <div className='pdf-loading'>
                <Spinner animation="border" role="status" />
                <p>Saved Template Loading...</p>
              </div>
            }
            {
              savedTemplatesError &&
              <div style={{ marginTop: '15px' }}>
                <Alert variant='danger'>
                  {savedTemplatesError}
                </Alert>
              </div>
            }
            <div className='saved-templates-section'>
              <CustomDataGrid
                rows={savedTemplatesRows}
                columns={savedTemplatesColumns}
                totalRows={savedTemplates.length}
                onRowClick={handleRowClick}
                selectedRowId={selectedRowId}
                toolbarProps={{
                  showViewPDF: true,
                  onViewPDF: handleViewPDF,
                  selectedRow: selectedTemplate
                }}
              />
              <ViewPdfModal
                isModalOpen={showPDF}
                handleClose={closeShowPDF}
                pdfImageSrc={savedPdfImage}
              />
            </div>
          </>
        )}
      {
        activeTab === 'create' && (
          <>
            {templateError && (
              <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>
                {templateError}
              </Alert>
            )}
            {
              templatesLoading ? (
                <div className="template-loading-container">
                  <Spinner animation="border" variant="primary" />
                  <p>Loading templates...</p>
                </div>
              ) : !templateError && (
                <TemplateSelector
                  selectedTemplate={state.selectedTemplate}
                  handleSelect={handleSelect}
                  templates={formData}
                />
              )
            }
            {isConfigLoading && (
              <div className="template-loading-container">
                <Spinner animation="border" variant="primary" />
                <p>Loading Template Form...</p>
              </div>

            )}

            {state.selectedTemplate &&
              selectedTemplateConfig &&
              Object.keys(selectedTemplateConfig).length > 0 &&
              Object.values(selectedTemplateConfig).some(value => Array.isArray(value) && value.length > 0) && (
                <div className={`proof-flex-container ${state.isModalOpen || (isDesktop && state.isLoading) ? "preview-open" : ""}`}>
                  <div className="proof-form-container">
                    <ReusableForm
                      selectedTemplateConfig={selectedTemplateConfig}
                      formFields={formFields}
                      onChange={handleInputChange}
                      handleGeneratePreview={handleGeneratePreview}
                    />
                    {!isDesktop && state.isLoading && (
                      <div className="template-loading-container" style={{ marginTop: '15px' }}>
                        <Spinner animation="border" variant="primary" size="sm" />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Generating {selectedTemplateConfig.name} preview...
                        </Typography>
                      </div>
                    )}
                  </div>

                  {isDesktop && (
                    <div className="proof-right-side-layout">
                      {state.isLoading ? (
                        <div className="side-loading-container">
                          <Spinner animation="border" variant="primary" />
                          <p>Generating {selectedTemplateConfig.name} preview...</p>
                        </div>
                      ) : state.isModalOpen ? (
                        <PdfPreviewModal
                          isModalOpen={state.isModalOpen}
                          handleClose={handleClose}
                          pdfImageSrc={state.pdfImageSrc}
                          selectedTemplate={selectedTemplateConfig.name}
                          handleDownloadPdf={() =>
                            handleDownloadPdf(state.pdfBinaryData, state.selectedTemplate)
                          }
                          error={state.error}
                          successMessage={state.successMessage}
                          templateName={state.templateName}
                          setTemplateName={(name) =>
                            setState((prevState) => ({ ...prevState, templateName: name }))
                          }
                          handleTemplateSave={handleTemplateSave}
                          isDesktop={isDesktop}
                        />
                      ) : (
                        <div className="side-placeholder-container">
                          <div className="placeholder-content">
                            <Typography variant="h6" sx={{ color: '#2f539b', fontWeight: 'bold', mb: 2 }}>
                              Preview Area
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              The generated PDF will be displayed here.
                              Fill in the form and click <strong>Generate Preview</strong>.
                            </Typography>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!isDesktop && (
                    <PdfPreviewModal
                      isModalOpen={state.isModalOpen}
                      handleClose={handleClose}
                      pdfImageSrc={state.pdfImageSrc}
                      selectedTemplate={selectedTemplateConfig.name}
                      handleDownloadPdf={() =>
                        handleDownloadPdf(state.pdfBinaryData, state.selectedTemplate)
                      }
                      error={state.error}
                      successMessage={state.successMessage}
                      templateName={state.templateName}
                      setTemplateName={(name) =>
                        setState((prevState) => ({ ...prevState, templateName: name }))
                      }
                      handleTemplateSave={handleTemplateSave}
                      isDesktop={false}
                    />
                  )}
                </div>
              )}

            {state.pdfError && (
              <Alert
                variant="danger"
                style={{ margin: "10px", whiteSpace: "pre-line" }}
              >
                {state.pdfError}
              </Alert>
            )}
          </>
        )}
    </div>

  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
};
export default ProofTemplates;